/**
 * Same behavior as the catalog-graph plugin's card, but the "View graph" link uses a
 * fixed `/catalog-graph` URL instead of {@link catalogGraphRouteRef}.
 *
 * With `createApp` + legacy `FlatRoutes`, `catalogGraphRouteRef` is not bound when
 * {@link CatalogGraphPage} is wrapped (e.g. in AdminOnlyRoute), which breaks
 * {@link EntityCatalogGraphCard} on every entity overview ("No path for routeRef … catalog-graph").
 */

import { useMemo, useCallback } from 'react';
import {
  getCompoundEntityRef,
  parseEntityRef,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import { InfoCard } from '@backstage/core-components';
import { useAnalytics, useRouteRef } from '@backstage/core-plugin-api';
import {
  EntityRelationsGraph,
  Direction,
} from '@backstage/plugin-catalog-graph';
import {
  entityRouteRef,
  humanizeEntityRef,
  useEntity,
} from '@backstage/plugin-catalog-react';
import { makeStyles } from '@material-ui/core/styles';
import classNames from 'classnames';
import { useNavigate } from 'react-router-dom';

const CATALOG_GRAPH_PATH = '/catalog-graph';

const useStyles = makeStyles(
  {
    card: ({ height }: { height?: number }) => ({
      display: 'flex',
      flexDirection: 'column',
      ...(height && {
        height,
        maxHeight: height,
        minHeight: height,
      }),
    }),
    graph: ({ height }: { height?: number }) => ({
      flex: height ? '0 0 auto' : 1,
      minHeight: 0,
      ...(height && { height }),
    }),
  },
  { name: 'EntityRelationsOverviewCard' },
);

export type EntityRelationsOverviewCardProps = {
  variant?: 'gridItem';
  height?: number;
  maxDepth?: number;
  unidirectional?: boolean;
  mergeRelations?: boolean;
  direction?: (typeof Direction)[keyof typeof Direction];
};

export function EntityRelationsOverviewCard({
  variant = 'gridItem',
  height = 400,
  maxDepth = 1,
  unidirectional = true,
  mergeRelations = true,
  direction = Direction.LEFT_RIGHT,
}: EntityRelationsOverviewCardProps) {
  const { entity } = useEntity();
  const entityName = useMemo(() => getCompoundEntityRef(entity), [entity]);
  const catalogEntityRoute = useRouteRef(entityRouteRef);
  const navigate = useNavigate();
  const analytics = useAnalytics();
  const classes = useStyles({ height });

  const defaultOnNodeClick = useCallback(
    (node: { id: string; entity: { metadata?: { title?: string } } }, _?: unknown) => {
      const nodeEntityName = parseEntityRef(node.id);
      const path = catalogEntityRoute({
        kind: nodeEntityName.kind.toLocaleLowerCase('en-US'),
        namespace: nodeEntityName.namespace.toLocaleLowerCase('en-US'),
        name: nodeEntityName.name,
      });
      analytics.captureEvent(
        'click',
        node.entity.metadata?.title ?? humanizeEntityRef(nodeEntityName),
        { attributes: { to: path } },
      );
      navigate(path);
    },
    [catalogEntityRoute, navigate, analytics],
  );

  const graphSearch = new URLSearchParams();
  graphSearch.append('rootEntityRefs[]', stringifyEntityRef(entity));
  graphSearch.append('maxDepth', String(maxDepth));
  graphSearch.append('unidirectional', String(unidirectional));
  graphSearch.append('mergeRelations', String(mergeRelations));
  graphSearch.append('direction', String(direction));
  const catalogGraphUrl = `${CATALOG_GRAPH_PATH}?${graphSearch.toString()}`;

  return (
    <InfoCard
      title="Relations"
      variant={variant}
      noPadding
      cardClassName={classes.card}
      deepLink={{
        title: 'View graph',
        link: catalogGraphUrl,
      }}
    >
      <EntityRelationsGraph
        rootEntityNames={entityName}
        onNodeClick={defaultOnNodeClick}
        className={classNames(classes.graph)}
        maxDepth={maxDepth}
        unidirectional={unidirectional}
        mergeRelations={mergeRelations}
        direction={direction}
        zoom="enable-on-click"
      />
    </InfoCard>
  );
}
